import type { Task } from '../types/crm';
import { CRM_TIMEZONE, formatCrmMonthYear, formatSingaporeDateTime12h, parseCrmDate } from './dateTime';

export const TASK_TITLE_OPTIONS = [
  'New Visit',
  'Sample Arrangement',
  'Urgent Delivery',
  'Goods Exchange',
  'Goods Return',
  'Goods Replenish',
  'Regular Visit',
  'Meeting/Appointment',
] as const;

export type TaskTitleOption = (typeof TASK_TITLE_OPTIONS)[number];

export const TASK_TITLE_OTHERS = 'Others';

export const TASK_TITLE_CHART_COLORS: Record<string, string> = {
  'Regular Visit': '#E76F51',
  'Urgent Delivery': '#F4A261',
  'Sample Arrangement': '#4A7BB7',
  'New Visit': '#E9B44C',
  'Meeting/Appointment': '#2A9D8F',
  'Goods Exchange': '#9B6BD4',
  'Goods Return': '#E57AB3',
  'Goods Replenish': '#3BAFDA',
  [TASK_TITLE_OTHERS]: '#A8B4C4',
};

export type TaskTitlePieSlice = {
  label: string;
  count: number;
  pct: number;
  color: string;
};

export function categorizeTaskTitle(title: string): string {
  const trimmed = title.trim();
  if (!trimmed) return TASK_TITLE_OTHERS;
  const match = TASK_TITLE_OPTIONS.find((opt) => opt.toLowerCase() === trimmed.toLowerCase());
  return match ?? TASK_TITLE_OTHERS;
}

export function taskDueMonthKey(task: Pick<Task, 'due_date' | 'created_at'>): string | null {
  const d = parseCrmDate(task.due_date) ?? parseCrmDate(task.created_at);
  if (!d) return null;
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: CRM_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
    .format(d)
    .slice(0, 7);
}

export function formatTaskMonthLabel(monthKey: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(monthKey.trim());
  if (!m) return formatCrmMonthYear(monthKey);
  const year = Number(m[1]);
  const month = Number(m[2]);
  if (!Number.isFinite(year) || month < 1 || month > 12) return formatCrmMonthYear(monthKey);
  // Fixed English short month — matches dashboard chart labels (Jan–Dec).
  const short = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${short[month - 1]} ${year}`;
}

export function currentMonthKeySingapore(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: CRM_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
    .format(new Date())
    .slice(0, 7);
}

export function listTaskMonthOptions(tasks: Task[]): { value: string; label: string }[] {
  const keys = new Set<string>();
  for (const task of tasks) {
    const key = taskDueMonthKey(task);
    if (key) keys.add(key);
  }
  return [...keys]
    .sort((a, b) => b.localeCompare(a))
    .map((value) => ({ value, label: formatTaskMonthLabel(value) }));
}

export function pickDefaultTaskChartMonth(
  monthOptions: { value: string }[],
  tasks: Task[],
  currentMonth = currentMonthKeySingapore(),
): string {
  if (monthOptions.length === 0) return '';
  const currentHasTasks = tasks.some((task) => taskDueMonthKey(task) === currentMonth);
  if (currentHasTasks) return currentMonth;
  return monthOptions[0]!.value;
}

export function buildTaskTitlePieSlices(tasks: Task[], monthKey: string): TaskTitlePieSlice[] {
  return buildTaskTitlePieSlicesFromTasks(tasks.filter((task) => taskDueMonthKey(task) === monthKey));
}

/** Pie slices for any task list (no month filter). */
export function buildTaskTitlePieSlicesFromTasks(
  tasks: Pick<Task, 'title'>[],
): TaskTitlePieSlice[] {
  const counts = new Map<string, number>();
  for (const task of tasks) {
    const label = categorizeTaskTitle(task.title ?? '');
    counts.set(label, (counts.get(label) ?? 0) + 1);
  }

  const total = tasks.length;
  if (total === 0) return [];

  return [...TASK_TITLE_OPTIONS, TASK_TITLE_OTHERS]
    .map((label) => {
      const count = counts.get(label) ?? 0;
      if (count <= 0) return null;
      return {
        label,
        count,
        pct: (count / total) * 100,
        color: TASK_TITLE_CHART_COLORS[label] ?? '#94a3b8',
      };
    })
    .filter((slice): slice is TaskTitlePieSlice => slice != null)
    .sort((a, b) => b.count - a.count);
}

export type TaskDisplayStatus = 'To Do' | 'In Progress' | 'Done' | 'Due';

export function taskDisplayStatus(task: Pick<Task, 'status' | 'due_date'>): TaskDisplayStatus {
  const status = task.status ?? 'To Do';
  if (status === 'Done') return 'Done';
  if (isTaskOverdue(task)) return 'Due';
  if (status === 'In Progress') return 'In Progress';
  return 'To Do';
}

export function isTaskOverdue(task: Pick<Task, 'status' | 'due_date'>): boolean {
  if ((task.status ?? 'To Do') === 'Done') return false;
  const due = parseCrmDate(task.due_date);
  if (!due) return false;
  return due.getTime() < Date.now();
}

export function taskStatusModifier(display: TaskDisplayStatus): string {
  switch (display) {
    case 'Done':
      return 'task-status--done';
    case 'Due':
      return 'task-status--overdue';
    case 'In Progress':
      return 'task-status--progress';
    default:
      return 'task-status--todo';
  }
}

/** Value for `<input type="datetime-local" />` in Singapore time. */
export function toDatetimeLocalSingapore(iso?: string | null): string {
  const d = parseCrmDate(iso);
  if (!d) return defaultDatetimeLocalSingapore();

  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-CA', {
      timeZone: CRM_TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    })
      .formatToParts(d)
      .map((p) => [p.type, p.value]),
  );

  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

export function defaultDatetimeLocalSingapore(): string {
  return toDatetimeLocalSingapore(new Date().toISOString());
}

/** Store due date the same way as desktop CRM (`yyyy-MM-ddTHH:mm:ss` in SG). */
export function datetimeLocalSingaporeToIso(local: string): string {
  const trimmed = local.trim();
  if (!trimmed) return '';

  const [datePart, timePart = '00:00'] = trimmed.split('T');
  const [hh = '00', mm = '00'] = timePart.split(':');
  return `${datePart}T${hh.padStart(2, '0')}:${mm.padStart(2, '0')}:00+08:00`;
}

const DUE_MONTHS: Record<string, number> = {
  jan: 1,
  feb: 2,
  mar: 3,
  apr: 4,
  may: 5,
  jun: 6,
  jul: 7,
  aug: 8,
  sep: 9,
  oct: 10,
  nov: 11,
  dec: 12,
};

function resolveDueHour(hour: number, meridiem?: string): number | null {
  const mer = meridiem?.trim().toLowerCase().replace(/\./g, '');
  if (mer === 'am') {
    if (hour < 1 || hour > 12) return null;
    return hour === 12 ? 0 : hour;
  }
  if (mer === 'pm') {
    if (hour < 1 || hour > 12) return null;
    return hour === 12 ? 12 : hour + 12;
  }
  if (hour < 0 || hour > 23) return null;
  return hour;
}

function dueDatetimeLocalParts(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
): string | null {
  if (
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31 ||
    hour < 0 ||
    hour > 23 ||
    minute < 0 ||
    minute > 59
  ) {
    return null;
  }

  const local = `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  const iso = datetimeLocalSingaporeToIso(local);
  return parseCrmDate(iso) ? local : null;
}

/** Human-readable due date for the task form text field (12-hour clock). */
export function formatDueDateInputText(local: string): string {
  const trimmed = local.trim();
  if (!trimmed) return '';
  const formatted = formatSingaporeDateTime12h(datetimeLocalSingaporeToIso(trimmed));
  return formatted === '—' ? '' : formatted;
}

/** Parse typed due date/time into datetime-local (Singapore) format. */
export function parseDueDateInput(text: string): string | null {
  const trimmed = text.trim();
  if (!trimmed) return null;

  const isoMatch = trimmed.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})[ T](\d{1,2}):(\d{2})(?:\s*(am|pm))?/i);
  if (isoMatch) {
    const hour = resolveDueHour(Number(isoMatch[4]), isoMatch[6]);
    if (hour === null) return null;
    return dueDatetimeLocalParts(
      Number(isoMatch[1]),
      Number(isoMatch[2]),
      Number(isoMatch[3]),
      hour,
      Number(isoMatch[5]),
    );
  }

  const dmyMatch = trimmed.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})[ T,]+(\d{1,2}):(\d{2})(?:\s*(am|pm))?/i);
  if (dmyMatch) {
    const hour = resolveDueHour(Number(dmyMatch[4]), dmyMatch[6]);
    if (hour === null) return null;
    return dueDatetimeLocalParts(
      Number(dmyMatch[3]),
      Number(dmyMatch[2]),
      Number(dmyMatch[1]),
      hour,
      Number(dmyMatch[5]),
    );
  }

  const friendly = trimmed.match(
    /^(\d{1,2})\s+([A-Za-z]{3,})\s+(\d{4})[\s,]*(?:at\s*)?(\d{1,2}):(\d{2})(?:\s*(am|pm))?/i,
  );
  if (friendly) {
    const month = DUE_MONTHS[friendly[2].slice(0, 3).toLowerCase()];
    if (month) {
      const hour = resolveDueHour(Number(friendly[4]), friendly[6]);
      if (hour === null) return null;
      return dueDatetimeLocalParts(
        Number(friendly[3]),
        month,
        Number(friendly[1]),
        hour,
        Number(friendly[5]),
      );
    }
  }

  return null;
}
